import { ChartColorDescription, ChartColorDescriptionProps } from '../ChartColorDescription';
import { keyframes, styled } from '@/stitches.config';
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/chevron-down.svg';
import { useEffect, useRef, useState } from 'react';
import { useScreenResize } from '@/modules/shared/hooks/useScreenResize';

const PX_MIN_HEIGHT = 68;

const collapseAnimation = keyframes({
  from: {
    height: 'var(--campaign-tracking_collapsible_height)',
  },
  to: {
    height: `${PX_MIN_HEIGHT}px`,
  },
});

const expandAnimation = keyframes({
  from: {
    height: `${PX_MIN_HEIGHT}px`,
  },
  to: {
    height: 'var(--campaign-tracking_collapsible_height)',
  },
});

const RootContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  gap: '$4',
  padding: '$6 $8',
  background: '$seafoam700',
  overflow: 'hidden',
  borderRadius: '$2',
  justifyContent: 'space-between',
  variants: {
    isCollapsed: {
      true: {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        animation: `${collapseAnimation} 0.3s none`,
      },
      false: {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        animation: `${expandAnimation} 0.3s none`,
      },
    },
  },
});

const ColorList = styled('ul', {
  display: 'flex',
  gap: '$4',
  listStyle: 'none',
  flexWrap: 'wrap',
  margin: 0,
  padding: 0,
});

const ColorBlockLI = styled('li', {
  listStyleType: 'none',
});

const IconButton = styled('button', {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '$darkBlue400',
  variants: {
    isCollapsed: {
      true: {
        transform: 'rotate(0deg)',
      },
      false: {
        transform: 'rotate(180deg)',
      },
    },
  },
});

const Icon = styled(ChevronDownIcon, {});

interface CollapsibleChartColorDescListProps {
  list: ChartColorDescriptionProps[];
}

export const CollapsibleChartColorDescList = ({ list }: CollapsibleChartColorDescListProps): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [containerHeight, setContainerHeight] = useState(0);
  const [neededHeight, setNeededHeight] = useState(0);
  const [shouldHideButton, setShouldHideButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useScreenResize();

  useEffect(() => {
    if (containerRef.current) {
      // 24 is the padding of the container
      setContainerHeight(
        containerRef.current.clientHeight +
          (containerRef.current.scrollHeight - containerRef.current.offsetHeight) +
          24,
      );
      if (
        containerRef.current.clientHeight === containerRef.current.offsetHeight &&
        containerRef.current.offsetHeight === containerRef.current.scrollHeight
      ) {
        setShouldHideButton(true);
      }
    }
  }, [containerRef.current, size]);

  const handleCollapse = (): void => {
    if (isCollapsed) {
      setIsCollapsed((prev) => !prev);
      setNeededHeight(containerHeight);
    } else {
      setIsCollapsed((prev) => !prev);
      setTimeout(() => {
        setNeededHeight(PX_MIN_HEIGHT);
      }, 280);
    }
  };

  const minHeightVar: React.CSSProperties = {
    // @ts-expect-error
    '--campaign-tracking_collapsible_height': `${containerHeight}px`,
  };

  return (
    <RootContainer
      isCollapsed={isCollapsed}
      ref={containerRef}
      style={{ maxHeight: neededHeight === 0 ? `${PX_MIN_HEIGHT}px` : neededHeight, ...minHeightVar }}
    >
      <ColorList>
        {list.map((item) => (
          <ColorBlockLI key={item.label}>
            <ChartColorDescription color={item.color} label={item.label} />
          </ColorBlockLI>
        ))}
      </ColorList>
      {!shouldHideButton && (
        <IconButton onClick={handleCollapse} isCollapsed={isCollapsed}>
          <Icon />
        </IconButton>
      )}
    </RootContainer>
  );
};
